import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useUser } from '../../contexts/UserContext';
import { authService } from '../../services/auth';
import { config } from '../../config';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';

export interface Comment {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    username: string;
    profile_image: string | null;
  };
  is_pinned: boolean;
  is_deleted: boolean;
  reply_count: number;
  replies?: Comment[];
  parent_comment_id?: string;
  depth?: number;
}

interface CommentsProps {
  targetType: 'script' | 'profile';
  targetId: string;
  targetOwnerId?: string; // For permission checks
}

const CommentsContainer = styled.div`
  margin-top: 40px;
  padding: 20px 0;
`;

const CommentsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #333;
`;

const CommentsTitle = styled.h3`
  color: #ffffff;
  font-size: 1.2rem;
  margin: 0;
`;

const SortSelect = styled.select`
  background-color: #2a2a2a;
  color: #ffffff;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.9rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
  
  option {
    background-color: #2a2a2a;
    color: #ffffff;
  }
`;

const CommentsList = styled.div`
  margin-top: 20px;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  color: #999;
  font-size: 0.9rem;
`;

const NoCommentsMessage = styled.div`
  text-align: center;
  color: #999;
  font-style: italic;
  padding: 40px 20px;
`;

const InfiniteScrollSentinel = styled.div`
  height: 20px;
  margin: 20px 0;
`;

const Comments: React.FC<CommentsProps> = ({ targetType, targetId, targetOwnerId }) => {
  const { user, isLoggedIn } = useUser();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState('newest');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [totalComments, setTotalComments] = useState(0);

  // Check if current user can moderate (delete/pin comments)
  // Note: Database stores as "admin" but TypeScript interface uses is_admin
  const canModerate = isLoggedIn && (
    user?.id === targetOwnerId || 
    (user?.user_permissions as any)?.admin === true ||
    user?.user_permissions?.is_admin === true
  );

  // Fetch comments function
  const fetchComments = useCallback(async (reset = false) => {
    if (loading) return;
    
    setLoading(true);
    const currentOffset = reset ? 0 : offset;
    
    try {
      const response = await fetch(
        `${config.apiUrl}/comments/${targetType}/${targetId}?offset=${currentOffset}&limit=20&sort=${sortBy}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }
      
      const data = await response.json();
      
      if (reset) {
        setComments(data.comments);
        setOffset(data.comments.length);
      } else {
        setComments(prev => [...prev, ...data.comments]);
        setOffset(prev => prev + data.comments.length);
      }
      
      setHasMore(data.has_more);
      if (data.total_count !== undefined) {
        setTotalComments(data.total_count);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  }, [targetType, targetId, sortBy, offset, loading]);

  // Load more comments for infinite scroll
  const loadMoreComments = useCallback(async () => {
    if (!hasMore || loading) return;
    fetchComments(false);
  }, [hasMore, loading, fetchComments]);

  // Initial load and sort change
  useEffect(() => {
    fetchComments(true);
  }, [sortBy, targetType, targetId]);

  // Handle sort change
  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(event.target.value);
    setOffset(0);
  };

  // Handle new comment submission
  const handleCommentSubmit = async (content: string, parentCommentId?: string) => {
    if (!isLoggedIn || !user) return;
    
    setSubmittingComment(true);
    
    try {
      const response = await fetch(`${config.apiUrl}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          content,
          target_type: targetType,
          target_id: targetId,
          parent_comment_id: parentCommentId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to post comment');
      }
      
      const data = await response.json();
      
      if (parentCommentId) {
        // This is a reply - refresh comments to show updated reply counts
        // Note: This will collapse expanded replies, but ensures consistency
        fetchComments(true);
      } else {
        // This is a top-level comment - insert after any pinned comment
        setComments(prev => {
          const pinnedIndex = prev.findIndex(comment => comment.is_pinned);
          if (pinnedIndex >= 0) {
            // Insert after the pinned comment
            const newComments = [...prev];
            newComments.splice(pinnedIndex + 1, 0, data.comment);
            return newComments;
          } else {
            // No pinned comment, add to the beginning
            return [data.comment, ...prev];
          }
        });
      }
      
      // Update total count for all comments (both top-level and replies)
      setTotalComments(prev => prev + 1);
      
    } catch (error) {
      console.error('Error posting comment:', error);
      alert('Failed to post comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle comment deletion
  const handleCommentDelete = async (commentId: string) => {
    if (!isLoggedIn) return;
    
    try {
      const response = await fetch(`${config.apiUrl}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }
      
      // Update comment in state to show as deleted
      setComments(prev => prev.map(comment => 
        comment.id === commentId 
          ? { ...comment, is_deleted: true, content: '[deleted]' }
          : comment
      ));
      
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment. Please try again.');
    }
  };

  // Handle comment pin/unpin
  const handleCommentPin = async (commentId: string, pinned: boolean) => {
    if (!canModerate) return;
    
    try {
      const response = await fetch(`${config.apiUrl}/comments/${commentId}/pin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({ pinned })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update pin status');
      }
      
      // Always refresh comments to ensure proper ordering
      // This handles the single-pin constraint and ensures pinned comment appears at top
      fetchComments(true);
      
    } catch (error) {
      console.error('Error updating pin status:', error);
      alert('Failed to update pin status. Please try again.');
    }
  };

  // Set up infinite scroll
  const { sentinelRef, loadingRef } = useInfiniteScroll({
    loadMore: loadMoreComments,
    hasMore: hasMore,
    loading: loading
  });

  return (
    <CommentsContainer>
      <CommentsHeader>
        <CommentsTitle>
          Comments ({totalComments})
        </CommentsTitle>
        <SortSelect value={sortBy} onChange={handleSortChange}>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </SortSelect>
      </CommentsHeader>

      {/* Comment form for logged-in users */}
      {isLoggedIn && (
        <CommentForm 
          onSubmit={handleCommentSubmit}
          submitting={submittingComment}
          placeholder="Write a comment..."
        />
      )}

      {/* Comments list */}
      <CommentsList>
        {comments.length === 0 && !loading ? (
          <NoCommentsMessage>
            {isLoggedIn ? 'Be the first to comment!' : 'No comments yet.'}
          </NoCommentsMessage>
        ) : (
          comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUser={user}
              canModerate={canModerate}
              onReply={handleCommentSubmit}
              onDelete={handleCommentDelete}
              onPin={handleCommentPin}
              targetType={targetType}
              targetId={targetId}
            />
          ))
        )}
      </CommentsList>

      {/* Loading indicator */}
      {loading && comments.length > 0 && (
        <LoadingIndicator ref={loadingRef}>
          Loading more comments...
        </LoadingIndicator>
      )}

      {/* Infinite scroll sentinel */}
      {hasMore && !loading && comments.length > 0 && (
        <InfiniteScrollSentinel ref={sentinelRef} />
      )}
    </CommentsContainer>
  );
};

export default Comments;